<template>
  <v-row no-gutters class="fill-height">
    <v-col>
      <card-title-row translation-path="sidebar.fieldCalculator.title" class="pt-2 pb-5"></card-title-row>
      <template v-if="isFetched">
        <field-calculator
          :value="values"
          :attributes="flattenAttributes"
          :is-less-than-two-selected="isLessThanTwoSelected"
          @addRow="addRow"
          @removeRow="removeRow"
          @change="changed"
          :relational-attributes-values="dataSourceAttributesRelational"
          :is-valid.sync="isFormValid"
          :isDataSourceUseAttributesQualifiedNames="isDataSourceUseAttributesQualifiedNames"
        >
        </field-calculator>
      </template>
      <main-loader v-else></main-loader>
    </v-col>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import CardTitleRow from '@/components/CardTitleRow';
import FieldCalculator from '@/components/FieldCalculator';

import deleteObject from '@/mixins/deleteObject';
import topo from '@/mixins/topo';

const row = { attribute: null, value: null, type: null };

export default {
  name: 'FieldCalculatorWrapper',
  mixins: [deleteObject, topo],
  components: {
    MainLoader,
    CardTitleRow,
    FieldCalculator,
  },
  data: () => ({
    values: [{ ...row, id: Date.now() }],
    dataSourceAttributesRelational: [],
    isFetched: false,
    isFormValid: false,
  }),
  computed: {
    layersMetadata: get('layers/metadata'),
    additionalLayersMetadata: get('layers/additionalMetadata'),
    dataSources() {
      return { ...this.layersMetadata, ...this.additionalLayersMetadata };
    },
    selectedFeatures: get('map/selectedFeatures'),
    currentLayerSelectedFeatures: get('map/selectedFeatures@:layerId'),
    currentLayerDataSourceName: get('layers/currentLayer@data_source_name'),
    dataSourceAttributes: get('layers/metadata@:currentLayerDataSourceName.attributes_schema.attributes'),
    isDataSourceUseAttributesQualifiedNames: get(
      'layers/metadata@:currentLayerDataSourceName.attributes_use_datasource_qualified_names'
    ),
    dataSourceIdName: get('layers/metadata@:currentLayerDataSourceName.attributes_schema.id_name'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    relationValuesMapping: get('attributesSchema/relationValuesMapping'),
    currentLayerDataSourceRelationValuesMapping: get('attributesSchema/relationValuesMappingCache'),
    layerId() {
      return this.$route.params.lid;
    },

    isLessThanTwoSelected() {
      return (this.currentLayerSelectedFeatures?.length || 0) < 2;
    },

    computedAttributes() {
      return this.dataSourceAttributes?.filter(attribute => attribute.data_type.name !== 'geometric');
    },
    currentLayerFormSchema: get('layers/currentLayer@form_schema.elements'),
    flattenAttributes() {
      return this.$_flattenStructure(this.currentLayerFormSchema, 'elements').map(attribute => {
        const attributeMeta = this.computedAttributes?.find(
          metadataAttribute => attribute.attribute === metadataAttribute.name
        );
        return {
          ...attributeMeta,
          ...attribute,
        };
      });
    },
    computedValid() {
      return this.isFormValid && !this.isDisabled;
    },
    currentDataSourceUseQualifiedNames() {
      return this.layersMetadata[this.currentLayerDataSourceName]?.attributes_use_datasource_qualified_names;
    },
  },
  watch: {
    flattenAttributes: {
      immediate: true,
      handler(nV) {
        if (nV?.length > 0) {
          this.setRelationalMetadata([...nV]);
        }
      },
    },
    computedValid(nV) {
      this.isStickyButtonsValid = nV;
    },
  },
  methods: {
    getDataSource: call('layers/getLayerMetadata'),
    calculateFieldsNonTopo: call('edit/calculateFields'),
    calculateFieldsTopo: call('edit/calculateFieldsTopo'),
    addRow() {
      this.$set(this.values, this.values.length, { ...row, id: Date.now() });
    },
    removeRow(index) {
      this.$delete(this.values, index);
    },
    changed(value, key, index) {
      this.$set(this.values[index], key, value);
    },
    async setRelationalMetadata(attributes) {
      this.isFetched = false;
      const relationedAttributes = attributes.filter(
        attribute =>
          attribute.relation &&
          attributes.filter(att => att.relation?.data_source === attribute.relation.data_source).length === 1
      );
      this.dataSourceAttributesRelational = this.fetchRelationalMetadata(attributes, relationedAttributes);
      this.isFetched = true;
    },
    fetchRelationalMetadata(attributes, relationedAttributes) {
      attributes = attributes.map(attribute => {
        return {
          ...attribute,
          value: this.currentDataSourceUseQualifiedNames
            ? attribute.name
            : `${attribute.datasource.name}.${attribute.name}`,
        };
      });
      try {
        const relationedDataSources = relationedAttributes
          .filter(
            (thing, index, self) => index === self.findIndex(t => t.relation.data_source === thing.relation.data_source)
          )
          .map(attribute => attribute.relation.data_source);
        const relationdDataSourcesMetadata = relationedDataSources.map(dataSource => {
          return {
            metadata: this.dataSources[dataSource],
            dataSource,
          };
        });
        const preparedResponses = relationdDataSourcesMetadata.reduce((total, current) => {
          const { attributes_use_datasource_qualified_names: useQualifiedNames } = current.metadata;
          return {
            ...total,
            [current.dataSource]: current.metadata.attributes_schema.attributes
              .filter(attribute => !attribute.relation)
              .map(attribute => {
                return {
                  ...attribute,
                  label: `${attribute.verbose_name} (${current.metadata.verbose_name})`,
                  value: useQualifiedNames ? attribute.name : `${attribute.datasource.name}.${attribute.name}`,
                };
              }),
          };
        }, {});
        const mappedNames = attributes.map(attribute => attribute.name);
        return relationedAttributes.reduce((total, attribute) => {
          const index = mappedNames.indexOf(attribute.name);
          total.splice(index + 1, 0, ...preparedResponses[attribute.relation.data_source]);
          return total;
        }, attributes);
      } catch (error) {
        console.log('~ error', error);
      }
    },
    goBack() {
      this.clearStickyButtons();
      this.$router.push({ name: this.$route.meta.parent });
    },
    clearStickyButtons() {
      this.isStickyButtonsVisible = false;
      this.stickyButtons = [];
      this.isStickyButtonsLoading = false;
    },
    init() {
      this.isStickyButtonsVisible = true;
      this.stickyButtons = [
        {
          action: 'featuresEditing',
          translationPath: 'button.save',
        },
        {
          action: 'goBack',
          translationPath: 'dialog.disagree',
          cancel: true,
        },
      ];
      this.attachListeners();
    },
    refreshTableAndMap() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', this.layerId);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.layerId] });
    },
    attachListeners() {
      this.$root.$on('featuresEditing', this.featuresEditingHandler);
      this.$root.$on('goBack', this.goBack);
    },
    pushMessage() {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.successEditObjects'),
      });
    },
    getExpressions() {
      return this.values.map(value => {
        return {
          FIXED: {
            [this.isDataSourceUseAttributesQualifiedNames
              ? value.attribute
              : `${this.currentLayerDataSourceName}.${value.attribute}`]: {
              [value.type]:
                value.type === 'attribue'
                  ? this.isDataSourceUseAttributesQualifiedNames
                    ? value.value
                    : `${this.currentLayerDataSourceName}.${value.value}`
                  : value.value,
            },
          },
        };
      });
    },
    getFilters() {
      return {
        $IN: {
          [this.isDataSourceUseAttributesQualifiedNames
            ? this.dataSourceIdName
            : `${this.currentLayerDataSourceName}.${this.dataSourceIdName}`]: this.currentLayerSelectedFeatures,
        },
      };
    },
    async editingHof(fn) {
      try {
        this.isStickyButtonsLoading = true;
        const r = await fn({
          calculator_expressions: this.getExpressions(),
          features_filter: this.getFilters(),
          dataSource: this.currentLayerDataSourceName,
          params: {
            layer_id: this.layerId,
          },
        });
        this.pushMessage();
        this.isStickyButtonsLoading = false;
        return r;
      } catch (error) {
        console.log(error);
        this.isStickyButtonsLoading = false;
        throw new Error(error);
      }
    },
    featuresEditingHandler() {
      if (this.isTopo) {
        this.topofeaturesEditing();
      } else {
        this.nonTopofeaturesEditing();
      }
    },
    async nonTopofeaturesEditing() {
      await this.editingHof(this.calculateFieldsNonTopo);
      this.refreshTableAndMap();
    },
    async topofeaturesEditing() {
      const r = await this.editingHof(this.calculateFieldsTopo);
      this.refreshLayersAndTable(r);
    },
  },
  mounted() {
    this.init();
    this.$once('hook:beforeDestroy', () => {
      this.$root.$off('featuresEditing');
      this.$root.$off('goBack');
      this.clearStickyButtons();
    });
  },
};
</script>

<style lang="scss" scoped></style>
